import moment from 'moment';
import { DailyNavClosedTransactionGroup } from 'api';
import { formatProviderCode, ISO_DATE_FORMAT } from 'utilities';

export const CLOSED_TRANSACTION_WINDOWS = ['3M', '6M', '1Y', '2Y', 'YTD'] as const;

export type ClosedTransactionWindow = (typeof CLOSED_TRANSACTION_WINDOWS)[number];

// The backend fetches two calendar years, so every chip up to 2Y is complete.
export const DEFAULT_CLOSED_TRANSACTION_WINDOW: ClosedTransactionWindow = '3M';

export type ClosedTransactionSortKey = 'date' | 'securityType' | 'structure' | 'price' | 'size' | 'shares' | 'sources';

export interface ClosedTransactionWindowRange {
  startDate: string;
  endDate: string;
}

// Both bounds are ISO dates, like the group dates they are compared against, so
// the window is a string comparison and no timezone can shift a trade a day.
export const closedTransactionWindowRange = (
  windowKey: ClosedTransactionWindow,
  reportDate: string
): ClosedTransactionWindowRange | null => {
  const end = moment(reportDate, ISO_DATE_FORMAT, true);
  if (!end.isValid()) {
    return null;
  }

  const start = (() => {
    switch (windowKey) {
      case 'YTD':
        return end.clone().startOf('year');
      case '6M':
        return end.clone().subtract(6, 'months');
      case '1Y':
        return end.clone().subtract(1, 'years');
      case '2Y':
        return end.clone().subtract(2, 'years');
      case '3M':
      default:
        return end.clone().subtract(3, 'months');
    }
  })();

  return { startDate: start.format(ISO_DATE_FORMAT), endDate: end.format(ISO_DATE_FORMAT) };
};

export const filterGroupsToWindow = (
  groups: DailyNavClosedTransactionGroup[],
  range: ClosedTransactionWindowRange | null
): DailyNavClosedTransactionGroup[] => {
  if (!range) {
    return groups;
  }
  return groups.filter(
    group => group.transaction_date >= range.startDate && group.transaction_date <= range.endDate
  );
};

// The backend's grouping key, spelled out: two groups that agree on all four are
// the same group. Carrying the include/exclude state on this rather than on the
// array index keeps a row excluded when the sort or the window changes.
export const closedTransactionGroupKey = (group: DailyNavClosedTransactionGroup): string =>
  [group.transaction_date, group.price_per_share, group.security_type ?? '', group.volume_usd ?? ''].join('|');

export const formatClosedTransactionSources = (group: DailyNavClosedTransactionGroup): string => {
  const providers = group.provider_codes.map(formatProviderCode).join(', ');
  const trades = group.transaction_count === 1 ? '1 trade' : `${group.transaction_count} trades`;
  return providers ? `${providers} - ${trades}` : trades;
};

// Stands in for a share class no provider named, so it can be filtered on like
// any other value instead of being unreachable.
export const BLANK_FILTER_VALUE = '\u2014';

export interface ClosedTransactionFilters {
  // Hidden rather than selected values, for the same reason the include
  // checkboxes track exclusions: a value the backend adds on a refetch arrives
  // visible instead of silently filtered out.
  hiddenSecurityTypes: Set<string>;
  hiddenProviders: Set<string>;
}

export const EMPTY_CLOSED_TRANSACTION_FILTERS: ClosedTransactionFilters = {
  hiddenSecurityTypes: new Set(),
  hiddenProviders: new Set(),
};

export const securityTypeFilterOptions = (groups: DailyNavClosedTransactionGroup[]): string[] => {
  const values = new Set(groups.map(group => group.security_type || BLANK_FILTER_VALUE));
  // The blank sits last: it is absent data, not a share class.
  const named = [...values].filter(value => value !== BLANK_FILTER_VALUE).sort();
  return values.has(BLANK_FILTER_VALUE) ? [...named, BLANK_FILTER_VALUE] : named;
};

export const providerFilterOptions = (groups: DailyNavClosedTransactionGroup[]): string[] =>
  [...new Set(groups.flatMap(group => group.provider_codes))].sort();

export const filterGroupsToColumns = (
  groups: DailyNavClosedTransactionGroup[],
  filters: ClosedTransactionFilters
): DailyNavClosedTransactionGroup[] => {
  const { hiddenSecurityTypes, hiddenProviders } = filters;
  if (hiddenSecurityTypes.size === 0 && hiddenProviders.size === 0) {
    return groups;
  }
  return groups.filter(group => {
    if (hiddenSecurityTypes.has(group.security_type || BLANK_FILTER_VALUE)) {
      return false;
    }
    // A group is kept while any one of its providers is still shown: hiding
    // Zanbato should not take away a trade Caplight also reported.
    return (
      group.provider_codes.length === 0 || group.provider_codes.some(code => !hiddenProviders.has(code))
    );
  });
};

const CLOSING_DATE_FORMAT = 'MMM D \'YY';

export const formatClosingDate = (transactionDate: string): string => {
  const parsed = moment(transactionDate, ISO_DATE_FORMAT, true);
  return parsed.isValid() ? parsed.format(CLOSING_DATE_FORMAT) : transactionDate;
};

const numberOrNull = (value: string | null): number | null => {
  if (value === null) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// Est. size and est. shares both sort on `volume_weight` -- the exact amount, or
// the midpoint of the range -- so a range sorts where it sits rather than at the
// edge the label happens to show.
const sortValue = (
  group: DailyNavClosedTransactionGroup,
  key: ClosedTransactionSortKey
): string | number | null => {
  switch (key) {
    case 'date':
      return group.transaction_date;
    case 'securityType':
      return group.security_type;
    case 'structure':
      return group.structure;
    case 'price':
      return numberOrNull(group.price_per_share);
    case 'size':
      return numberOrNull(group.volume_weight);
    case 'shares': {
      const weight = numberOrNull(group.volume_weight);
      const price = numberOrNull(group.price_per_share);
      return weight !== null && price !== null && price > 0 ? weight / price : null;
    }
    case 'sources':
      return formatClosedTransactionSources(group);
    default:
      return null;
  }
};

// A missing value sorts last in either direction: it is absent data, not a
// smallest value, and burying it keeps the top of the table readable.
export const sortGroups = (
  groups: DailyNavClosedTransactionGroup[],
  key: ClosedTransactionSortKey,
  direction: 'asc' | 'desc'
): DailyNavClosedTransactionGroup[] => {
  const sign = direction === 'asc' ? 1 : -1;
  return [...groups].sort((left, right) => {
    const a = sortValue(left, key);
    const b = sortValue(right, key);
    if (a === null || a === '') {
      return b === null || b === '' ? 0 : 1;
    }
    if (b === null || b === '') {
      return -1;
    }
    if (typeof a === 'number' && typeof b === 'number') {
      return (a - b) * sign;
    }
    return String(a).localeCompare(String(b)) * sign;
  });
};

export interface ClosedTransactionStats {
  low: number | null;
  high: number | null;
  median: number | null;
  // The volume-weighted average price.
  average: number | null;
  // Rows behind low / median / high.
  priceCount: number;
  // Rows behind the average.
  weightedCount: number;
  // Rows in the population that reported no volume, so they are out of the average.
  unweightedCount: number;
}

const median = (values: number[]): number | null => {
  if (values.length === 0) {
    return null;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle];
};

// `groups` is already the population: inside the window and still checked.
export const closedTransactionStats = (groups: DailyNavClosedTransactionGroup[]): ClosedTransactionStats => {
  const prices = groups.map(group => numberOrNull(group.price_per_share)).filter((p): p is number => p !== null);

  const weighted = groups.reduce(
    (totals, group) => {
      const price = numberOrNull(group.price_per_share);
      const weight = numberOrNull(group.volume_weight);
      if (price === null || weight === null || weight <= 0) {
        return totals;
      }
      return { value: totals.value + price * weight, weight: totals.weight + weight, count: totals.count + 1 };
    },
    { value: 0, weight: 0, count: 0 }
  );

  return {
    low: prices.length ? Math.min(...prices) : null,
    high: prices.length ? Math.max(...prices) : null,
    median: median(prices),
    average: weighted.weight > 0 ? weighted.value / weighted.weight : null,
    priceCount: prices.length,
    weightedCount: weighted.count,
    unweightedCount: prices.length - weighted.count,
  };
};
